/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Superadmin Contract - oRPC contract definitions for cross-org superadmin endpoints
 *
 * All endpoints require superuser authentication.
 * Includes cross-org dashboard, organization management, member management, and system health.
 */
import { oc } from "@orpc/contract";
import * as z from "zod";

import { DEFAULT_ORG_PLAN, OrgPlanSchema } from "../common/org-plan";
import { commonErrors } from "./base";
import { DateFilterSchema } from "./domain-schemas/shared/list-filters.schema";

// ============================================================================
// Schemas
// ============================================================================

export const TENANT_SECRET_KEYS = [
	// WhatsApp
	"ACCESS_TOKEN",
	"APP_SECRET",
	"PHONE_NUMBER_ID",
	"WABA_ID",
	"WHATSAPP_WEBHOOK_VERIFY_TOKEN",
	// Messenger
	"MESSENGER_PAGE_ACCESS_TOKEN",
	"MESSENGER_APP_SECRET",
	"MESSENGER_VERIFY_TOKEN",
	"MESSENGER_PAGE_ID",
	// Slack
	"SLACK_BOT_TOKEN",
	"SLACK_SIGNING_SECRET",
	"SLACK_CLIENT_ID",
	"SLACK_CLIENT_SECRET",
	// Discord
	"DISCORD_BOT_TOKEN",
	"DISCORD_PUBLIC_KEY",
	"DISCORD_APPLICATION_ID",
	// Telegram
	"TELEGRAM_BOT_TOKEN",
	"TELEGRAM_BOT_ID",
	"TELEGRAM_WEBHOOK_ROUTE_ID",
	"TELEGRAM_WEBHOOK_SECRET_TOKEN",
	// Instagram
	"INSTAGRAM_ACCESS_TOKEN",
	"INSTAGRAM_APP_SECRET",
	"INSTAGRAM_VERIFY_TOKEN",
	"INSTAGRAM_ACCOUNT_ID",
	// X (X_CHAT_PIN is backend-managed and intentionally absent)
	"X_ACCESS_TOKEN",
	"X_REFRESH_TOKEN",
	"X_ACCESS_TOKEN_SECRET",
	"X_BEARER_TOKEN",
	"X_USER_ID",
	"X_USERNAME",
	"X_CLIENT_ID",
	"X_CLIENT_SECRET",
	"X_API_KEY",
	"X_API_SECRET",
	"X_WEBHOOK_ID",
	"X_AI_REPLIES_APPROVED",
	// Microsoft Teams
	"TEAMS_APP_ID",
	"TEAMS_APP_PASSWORD",
	"TEAMS_APP_TENANT_ID",
	// Google Chat
	"GOOGLE_CHAT_CREDENTIALS",
	// GitHub
	"GITHUB_TOKEN",
	"GITHUB_APP_ID",
	"GITHUB_PRIVATE_KEY",
	"GITHUB_INSTALLATION_ID",
	"GITHUB_WEBHOOK_SECRET",
	// Linear
	"LINEAR_API_KEY",
	"LINEAR_CLIENT_ID",
	"LINEAR_CLIENT_SECRET",
	"LINEAR_WEBHOOK_SECRET",
	// AI
	"OPENROUTER_API_KEY",
] as const;

export const TenantSecretKeySchema = z.enum(TENANT_SECRET_KEYS);

/**
 * Superadmins may replace or clear only registered tenant settings. Values are
 * write-only: read models expose configured key names, never secret contents.
 */
export const UpdateOrganizationSecretsBodySchema = z.object({
	secrets: z.partialRecord(TenantSecretKeySchema, z.string()),
});

/** Per-org breakdown in dashboard */
export const OrgBreakdownSchema = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	plan: OrgPlanSchema,
	isActive: z.boolean(),
	userCount: z.number().int(),
	eventCount: z.number().int(),
	messageCount: z.number().int(),
});

export const SuperadminAiBreakdownRowSchema = z.object({
	value: z.string(),
	count: z.number().int(),
	rate: z.number(),
});

export const SuperadminAiSummarySchema = z.object({
	totalGenerations: z.number().int(),
	missingUsageCount: z.number().int(),
	missingUsageRate: z.number(),
	missingPerformanceCount: z.number().int(),
	missingPerformanceRate: z.number(),
	p95ResponseTimeMs: z.number().nullable(),
	averageResponseTimeMs: z.number().nullable(),
	costPer1kTokensUsd: z.number().nullable(),
	costPerResponseUsd: z.number().nullable(),
	toolErrorCount: z.number().int(),
	toolCallCount: z.number().int(),
	toolErrorRate: z.number().nullable(),
	cacheReadTokens: z.number().int(),
	cacheWriteInputTokens: z.number().int(),
	cacheHitRate: z.number().nullable(),
	reasoningTokens: z.number().int(),
	reasoningTokenRate: z.number().nullable(),
	/** Estimated USD saved by prompt caching (cache reads vs. full input price). */
	cacheSavingsUsd: z.number(),
	/** USD spent on generations that errored or were aborted. */
	wastedSpendUsd: z.number(),
	/** Generations that surfaced a non-fatal provider warning. */
	warningCount: z.number().int(),
});

export const SuperadminAiModelMetricSchema = z.object({
	provider: z.string(),
	model: z.string(),
	generationCount: z.number().int(),
	p95ResponseTimeMs: z.number().nullable(),
	p95TimeToFirstOutputMs: z.number().nullable(),
	averageOutputTokensPerSecond: z.number().nullable(),
	costPer1kTokensUsd: z.number().nullable(),
	toolErrorRate: z.number().nullable(),
	cacheHitRate: z.number().nullable(),
	reasoningTokenRate: z.number().nullable(),
	/** Actual billed cost (provider usage accounting) for the window. */
	actualCostUsd: z.number(),
	/** Our catalog-pricing estimate, for reconciliation. */
	estimatedCostUsd: z.number(),
	/** (actual − estimated) / estimated × 100; null when no estimate. */
	costDriftPct: z.number().nullable(),
});

export const SuperadminAiOrgMetricSchema = z.object({
	orgId: z.string(),
	orgName: z.string(),
	orgSlug: z.string(),
	generationCount: z.number().int(),
	p95ResponseTimeMs: z.number().nullable(),
	toolErrorRate: z.number().nullable(),
	/** Actual billed AI cost for the org in the window. */
	totalCostUsd: z.number(),
	/** USD spent on errored/aborted generations for the org. */
	wastedSpendUsd: z.number(),
});

export const SuperadminAiDailyMetricSchema = z.object({
	date: z.string(),
	generationCount: z.number().int(),
	p95ResponseTimeMs: z.number().nullable(),
	totalCostUsd: z.number(),
	toolErrorRate: z.number().nullable(),
	averageOutputTokensPerSecond: z.number().nullable(),
});

export const SuperadminAiObservabilitySchema = z.object({
	windowDays: z.number().int(),
	summary: SuperadminAiSummarySchema,
	byModel: z.array(SuperadminAiModelMetricSchema),
	byOrg: z.array(SuperadminAiOrgMetricSchema),
	statusBreakdown: z.array(SuperadminAiBreakdownRowSchema),
	finishReasonBreakdown: z.array(SuperadminAiBreakdownRowSchema),
	dailyTrend: z.array(SuperadminAiDailyMetricSchema),
});

/** Dashboard aggregate metrics */
export const SuperadminDashboardSchema = z.object({
	totalOrgs: z.number().int(),
	activeOrgs: z.number().int(),
	totalUsers: z.number().int(),
	totalEvents: z.number().int(),
	totalMessages: z.number().int(),
	totalConversations: z.number().int(),
	memberRoleBreakdown: z.array(
		z.object({
			role: z.string(),
			count: z.number().int().nonnegative(),
		})
	),
	perOrgBreakdown: z.array(OrgBreakdownSchema),
	aiObservability: SuperadminAiObservabilitySchema,
});

/**
 * Ceilings for per-org custom quota overrides. Generous upper bounds so
 * support can grant exceptions without hitting a wall, while still
 * catching obvious typos (6-digit seat count, petabyte-range storage).
 */
export const MAX_CUSTOM_STAFF_SEAT_LIMIT = 10_000;
export const MAX_CUSTOM_AI_PRESET_LIMIT = 10_000;
export const MAX_CUSTOM_TOOL_LIMIT = 1000;
export const MAX_CUSTOM_MONTHLY_MESSAGE_LIMIT = 10_000_000;
/**
 * 9 PB ceiling. Expressed as bytes on the wire -- fits in a JS safe
 * integer (Number.MAX_SAFE_INTEGER ~= 9 PiB). Repo casts to BigInt.
 */
export const MAX_CUSTOM_STORAGE_LIMIT_BYTES = 9_000_000_000_000_000;

/** Organization with stats */
export const OrganizationWithStatsSchema = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	plan: OrgPlanSchema,
	isActive: z.boolean(),
	hasSecrets: z.boolean(),
	secretKeys: z.array(z.string()),
	whatsappPhoneNumberId: z.string().nullable(),
	messengerPageId: z.string().nullable(),
	subscriptionStatus: z.string().optional(),
	billingInterval: z.string().nullable().optional(),
	/**
	 * Per-org quota overrides. `null` means "no override -- use plan default".
	 * Any non-null positive integer is the active override, chosen by
	 * `resolveOrgLimits()` via `??`.
	 */
	customMonthlyMessageLimit: z.number().int().nullable(),
	customStaffSeatLimit: z.number().int().nullable(),
	customAiPresetLimit: z.number().int().nullable().default(null),
	/** Enabled-custom-action quota override (null = plan default). */
	customToolLimitOverride: z.number().int().nullable().default(null),
	/**
	 * Serialized as a JS number (bytes). Safe up to Number.MAX_SAFE_INTEGER
	 * (~9 PiB), far beyond any realistic tenant storage grant. Server casts
	 * to BigInt before writing to the DB column.
	 */
	customStorageLimitBytes: z.number().int().nullable(),
	userCount: z.number().int(),
	eventCount: z.number().int(),
	messageCount: z.number().int(),
	createdAt: z.string().datetime(),
	/** Org-facing credit balance (never negative). */
	balanceCredits: z.number().int(),
	// Admin-controlled status flags. `isPastDue` is system-managed
	// (billing sync) and read-only through this contract.
	isSuspended: z.boolean(),
	isQuarantined: z.boolean(),
	isPastDue: z.boolean(),
	statusReason: z.string().nullable(),
	suspendedAt: z.string().datetime().nullable(),
	quarantinedAt: z.string().datetime().nullable(),
	/** Messages sent in the current UTC calendar month (quota meter). */
	messagesThisMonth: z.number().int(),
	/** Sum of RagDocument.sizeBytes for the org (storage quota meter). */
	storageUsedBytes: z.number().int(),
	/** Latest Message.sentAt for the org — "is this tenant alive?". */
	lastActivityAt: z.string().datetime().nullable(),
});

/**
 * Per-column AG-Grid text-filter model for `superadmin.listMembers`.
 *
 * Mirrors the operator set the `agTextColumnFilter` emits via
 * `api.getFilterModel()` so the server can apply the right Prisma predicate
 * (`contains`, `startsWith`, `equals`, `blank`, etc.) instead of approximating
 * with a single substring match. Earlier client-side iterations dropped the
 * operator and just passed `filter` as a global substring — which made the
 * `Blank` and `Equals` headers behave identically to `contains`.
 */
const MemberTextFilterModelSchema = z.object({
	type: z.enum([
		"contains",
		"notContains",
		"equals",
		"notEqual",
		"startsWith",
		"endsWith",
		"blank",
		"notBlank",
	]),
	filter: z.string().max(120).optional(),
});

/**
 * Query-string booleans arrive as `"true"` / `"false"` after deep-object
 * parsing. Preserve actual booleans for direct contract callers while
 * coercing only those two canonical wire values (unlike `z.coerce.boolean`,
 * which would incorrectly turn the string `"false"` into `true`).
 */
const QueryBooleanSchema = z.preprocess((value) => {
	if (value === "true") {
		return true;
	}
	if (value === "false") {
		return false;
	}
	return value;
}, z.boolean());

const MemberBooleanFilterModelSchema = z.object({
	value: QueryBooleanSchema,
});

/**
 * Per-column filter map for member listings. AND-wise on top of the global
 * `search`. Empty / missing entries skip the predicate.
 */
export const MemberColumnFiltersSchema = z
	.object({
		name: MemberTextFilterModelSchema.optional(),
		email: MemberTextFilterModelSchema.optional(),
		orgName: MemberTextFilterModelSchema.optional(),
		role: MemberTextFilterModelSchema.optional(),
		customRoleName: MemberTextFilterModelSchema.optional(),
		// DateTime? column: accepts the shared date operator envelope
		// (before/after/between/blank/notBlank — see list-filters.schema)
		// alongside the legacy text model (whose blank/notBlank still work).
		lastSignInAt: z
			.union([DateFilterSchema, MemberTextFilterModelSchema])
			.optional(),
		isActive: MemberBooleanFilterModelSchema.optional(),
		isSuperuser: MemberBooleanFilterModelSchema.optional(),
	})
	.optional();
export type MemberColumnFilters = z.infer<typeof MemberColumnFiltersSchema>;
export type MemberTextFilter = z.infer<typeof MemberTextFilterModelSchema>;

/**
 * Mirrors AG-Grid's `IGetRowsParams.sortModel`. We only honor colIds that map
 * to real server-side columns; anything else is dropped server-side without
 * an error so the contract is forward-compatible with future grid columns.
 */
const MemberSortItemSchema = z.object({
	colId: z.enum([
		"name",
		"email",
		"orgName",
		"role",
		"isActive",
		"isSuperuser",
		"lastSignInAt",
	]),
	sort: z.enum(["asc", "desc"]),
});
export const MemberSortModelSchema = z.array(MemberSortItemSchema).optional();
export type MemberSortModel = z.infer<typeof MemberSortModelSchema>;

/**
 * Per-column AG-Grid text-filter model for `superadmin.listOrganizations`.
 * Same operator surface as the Members equivalent — mirrors what
 * `agTextColumnFilter` emits via `api.getFilterModel()`.
 */
const OrgTextFilterModelSchema = z.object({
	type: z.enum([
		"contains",
		"notContains",
		"equals",
		"notEqual",
		"startsWith",
		"endsWith",
		"blank",
		"notBlank",
	]),
	filter: z.string().max(120).optional(),
});

const OrgBooleanFilterModelSchema = z.object({
	value: QueryBooleanSchema,
});

/**
 * Per-column filter map for organization listings. AND-wise on top of the
 * global `search`. Empty / missing entries skip the predicate.
 */
export const OrgColumnFiltersSchema = z
	.object({
		name: OrgTextFilterModelSchema.optional(),
		slug: OrgTextFilterModelSchema.optional(),
		plan: OrgTextFilterModelSchema.optional(),
		isActive: OrgBooleanFilterModelSchema.optional(),
	})
	.optional();
export type OrgColumnFilters = z.infer<typeof OrgColumnFiltersSchema>;
export type OrgTextFilter = z.infer<typeof OrgTextFilterModelSchema>;

/**
 * Mirrors AG-Grid's `IGetRowsParams.sortModel` for the org grid. Only
 * server-backed columns are honored; unrecognized colIds are dropped without
 * an error so the contract is forward-compatible with future grid columns.
 */
const OrgSortItemSchema = z.object({
	colId: z.enum([
		"name",
		"slug",
		"plan",
		"isActive",
		"balanceCredits",
		"createdAt",
	]),
	sort: z.enum(["asc", "desc"]),
});
export const OrgSortModelSchema = z.array(OrgSortItemSchema).optional();
export type OrgSortModel = z.infer<typeof OrgSortModelSchema>;

/** Member across orgs */
export const SuperadminMemberSchema = z.object({
	id: z.string(),
	orgId: z.string(),
	orgName: z.string(),
	/**
	 * Better Auth org-Member row id for (userId, orgId). Distinct from
	 * `id` (which is User.id). Required to assign/unassign custom roles
	 * via `roles.assignMember` / `roles.unassignMember`. Nullable when
	 * the user has no Member row in the joined org (e.g., orgId is empty).
	 */
	memberId: z.string().nullable(),
	/** Member.customRoleId — null when no custom role is assigned. */
	customRoleId: z.string().nullable(),
	/** Joined Role.name for fast-path display; null when no role assigned. */
	customRoleName: z.string().nullable(),
	name: z.string().nullable(),
	email: z.string(),
	role: z.string(),
	isSuperuser: z.boolean(),
	isActive: z.boolean(),
	joinedAt: z.string().datetime().nullable(),
	lastSignInAt: z.string().datetime().nullable(),
});

/** Channel contact (messaging user without an account) */
export const ChannelContactSchema = z.object({
	id: z.string(),
	name: z.string().nullable(),
	phoneE164: z.string().nullable(),
	orgId: z.string(),
	orgName: z.string(),
	channel: z.string().nullable(),
	lastMessageAt: z.string().datetime().nullable(),
	createdAt: z.string().datetime(),
});

/** Job queue status */
export const JobQueueStatusSchema = z.object({
	name: z.string(),
	waiting: z.number().int(),
	active: z.number().int(),
	completed: z.number().int(),
	failed: z.number().int(),
	delayed: z.number().int(),
});

/** System health metrics */
export const SystemHealthSchema = z.object({
	jobQueues: z.array(JobQueueStatusSchema),
	wsConnections: z.object({
		total: z.number().int(),
		authenticated: z.number().int(),
		rooms: z.number().int(),
	}),
	uptime: z.number(),
});

// ============================================================================
// Contract Definition
// ============================================================================

export const superadminContract = {
	// ==========================================================================
	// Dashboard
	// ==========================================================================

	/** Get cross-org dashboard metrics */
	getDashboard: oc
		.route({
			method: "GET",
			path: "/api/superadmin/dashboard",
			summary: "Get cross-org dashboard metrics",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string().optional(),
			})
		)
		.output(SuperadminDashboardSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	// ==========================================================================
	// Organizations
	// ==========================================================================

	/** List all organizations with stats */
	listOrganizations: oc
		.route({
			method: "GET",
			path: "/api/superadmin/organizations",
			summary: "List all organizations with stats",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string().optional(),
				search: z.string().optional(),
				filters: OrgColumnFiltersSchema,
				sort: OrgSortModelSchema,
				limit: z.coerce.number().int().min(1).max(1000).optional().default(100),
				offset: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(
			z.object({
				items: z.array(OrganizationWithStatsSchema),
				total: z.number().int(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	/** Get organization detail */
	getOrganization: oc
		.route({
			method: "GET",
			path: "/api/superadmin/organizations/{id}",
			summary: "Get organization detail",
			tags: ["Superadmin"],
		})
		.input(z.object({ id: z.string() }))
		.output(OrganizationWithStatsSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Create new organization */
	createOrganization: oc
		.route({
			method: "POST",
			path: "/api/superadmin/organizations",
			summary: "Create new organization",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				name: z.string().min(1).max(100),
				slug: z
					.string()
					.min(1)
					.max(50)
					.regex(/^[a-z0-9-]+$/),
				plan: OrgPlanSchema.default(DEFAULT_ORG_PLAN),
			})
		)
		.output(
			z.object({
				id: z.string(),
				name: z.string(),
				slug: z.string(),
				plan: OrgPlanSchema,
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			CONFLICT: { message: "Organization already exists" },
		}),

	/** Update organization */
	updateOrganization: oc
		.route({
			method: "PATCH",
			path: "/api/superadmin/organizations/{id}",
			summary: "Update organization",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				id: z.string(),
				name: z.string().min(1).max(100).optional(),
				slug: z
					.string()
					.min(1)
					.max(50)
					.regex(/^[a-z0-9-]+$/)
					.optional(),
				plan: OrgPlanSchema.optional(),
				whatsappPhoneNumberId: z.string().nullable().optional(),
				whatsappBusinessAccountId: z.string().nullable().optional(),
				messengerPageId: z.string().nullable().optional(),
				/**
				 * Superadmin-only plan-limit overrides. Tri-state semantics:
				 *   field absent  → don't touch the stored value
				 *   field = null  → clear override (fall back to plan default)
				 *   field = > 0   → set the override
				 * `resolveOrgLimits()` in @event-manager/api-client uses `??`, so
				 * `null` is the canonical "no override" marker, not `0`.
				 */
				customMonthlyMessageLimit: z
					.number()
					.int()
					.positive()
					.max(MAX_CUSTOM_MONTHLY_MESSAGE_LIMIT)
					.nullable()
					.optional(),
				customStaffSeatLimit: z
					.number()
					.int()
					.positive()
					.max(MAX_CUSTOM_STAFF_SEAT_LIMIT)
					.nullable()
					.optional(),
				customAiPresetLimit: z
					.number()
					.int()
					.positive()
					.max(MAX_CUSTOM_AI_PRESET_LIMIT)
					.nullable()
					.optional(),
				customToolLimitOverride: z
					.number()
					.int()
					.positive()
					.max(MAX_CUSTOM_TOOL_LIMIT)
					.nullable()
					.optional(),
				customStorageLimitBytes: z
					.number()
					.int()
					.positive()
					.max(MAX_CUSTOM_STORAGE_LIMIT_BYTES)
					.nullable()
					.optional(),
				/**
				 * Moderation flags. Flipping a flag on stamps its `*At`
				 * timestamp server-side; flipping it off clears it.
				 * `isPastDue` is system-managed and not accepted here.
				 */
				isSuspended: z.boolean().optional(),
				isQuarantined: z.boolean().optional(),
				statusReason: z.string().max(500).nullable().optional(),
			})
		)
		.output(OrganizationWithStatsSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Update org secrets */
	updateOrganizationSecrets: oc
		.route({
			method: "PUT",
			path: "/api/superadmin/organizations/{id}/secrets",
			summary: "Update org secrets",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				id: z.string(),
				secrets: z.partialRecord(TenantSecretKeySchema, z.string()),
			})
		)
		.output(z.object({ success: z.boolean(), secretKeys: z.array(z.string()) }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Activate/deactivate org */
	toggleOrganization: oc
		.route({
			method: "PATCH",
			path: "/api/superadmin/organizations/{id}/toggle",
			summary: "Activate/deactivate org",
			tags: ["Superadmin"],
		})
		.input(z.object({ id: z.string(), isActive: z.boolean() }))
		.output(z.object({ id: z.string(), isActive: z.boolean() }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Delete organization */
	deleteOrganization: oc
		.route({
			method: "DELETE",
			path: "/api/superadmin/organizations/{id}",
			summary: "Delete organization permanently",
			tags: ["Superadmin"],
		})
		.input(z.object({ id: z.string() }))
		.output(z.object({ success: z.boolean() }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	// ==========================================================================
	// Impersonation (super-user "Enter as org")
	// ==========================================================================

	/** Begin a super-user impersonation session targeting `orgId`. */
	enterOrg: oc
		.route({
			method: "POST",
			path: "/api/superadmin/impersonation/enter",
			summary:
				"Begin a super-user impersonation session targeting an organization",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string().min(1),
				reason: z.string().max(500).optional(),
			})
		)
		.output(
			z.object({
				impersonationId: z.string(),
				orgId: z.string(),
				orgName: z.string(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** End the current super-user impersonation session. */
	exitOrg: oc
		.route({
			method: "POST",
			path: "/api/superadmin/impersonation/exit",
			summary: "End the current super-user impersonation session",
			tags: ["Superadmin"],
		})
		.input(z.object({}))
		.output(
			z.object({
				impersonationId: z.string().nullable(),
				durationMs: z.number().int().nullable(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	/** Get OpenRouter key balance for an org */
	getOrgKeyBalance: oc
		.route({
			method: "GET",
			path: "/api/superadmin/organizations/{id}/key-balance",
			summary: "Get OpenRouter key credit balance for an org",
			tags: ["Superadmin"],
		})
		.input(z.object({ id: z.string() }))
		.output(
			z.object({
				hasKey: z.boolean(),
				usage: z.number().optional(),
				limit: z.number().nullable().optional(),
				label: z.string().optional(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	// ==========================================================================
	// Members
	// ==========================================================================

	/** List all members across orgs */
	listMembers: oc
		.route({
			method: "GET",
			path: "/api/superadmin/members",
			summary: "List all members across orgs",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string().optional(),
				search: z.string().optional(),
				role: z.string().optional(),
				filters: MemberColumnFiltersSchema,
				sort: MemberSortModelSchema,
				limit: z.coerce.number().int().min(1).max(1000).optional().default(50),
				offset: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(
			z.object({
				members: z.array(SuperadminMemberSchema),
				total: z.number().int(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	/** Update member role/status */
	updateMember: oc
		.route({
			method: "PATCH",
			path: "/api/superadmin/members/{id}",
			summary: "Update member role/status",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				id: z.string(),
				orgId: z.string().optional(),
				name: z.string().min(1).optional(),
				email: z.email().optional(),
				role: z.string().optional(),
				isActive: z.boolean().optional(),
				isSuperuser: z.boolean().optional(),
			})
		)
		.output(SuperadminMemberSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			CONFLICT: { message: "Member with this email already exists" },
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Create member in an org */
	createMember: oc
		.route({
			method: "POST",
			path: "/api/superadmin/members",
			summary: "Create member in an organization",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string(),
				name: z.string().min(1).max(100),
				email: z.email(),
				password: z.string().min(8),
				/** Per-org Member.role assigned to the new staff member. */
				role: z
					.enum(["owner", "admin", "secretary", "guest"])
					.default("secretary"),
				/**
				 * Platform-level User.role flag. `true` → "superuser"
				 * (cross-org), defaults to "user" otherwise. Server rejects
				 * the flag for non-superuser callers.
				 */
				isSuperuser: z.boolean().optional(),
			})
		)
		.output(SuperadminMemberSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			CONFLICT: { message: "Member with this email already exists" },
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Delete member from org */
	deleteMember: oc
		.route({
			method: "DELETE",
			path: "/api/superadmin/members/{id}",
			summary: "Delete member from organization",
			tags: ["Superadmin"],
		})
		.input(z.object({ id: z.string() }))
		.output(z.object({ success: z.boolean() }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	/** Bulk-delete many members in a single call */
	deleteMembers: oc
		.route({
			method: "POST",
			path: "/api/superadmin/members/bulk-delete",
			summary: "Bulk-delete members",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				ids: z.array(z.string().min(1)).min(1).max(500),
			})
		)
		.output(z.object({ count: z.number().int().min(0) }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
		}),

	// ==========================================================================
	// Channel Contacts
	// ==========================================================================

	/** List channel contacts (messaging users without accounts) */
	listChannelContacts: oc
		.route({
			method: "GET",
			path: "/api/superadmin/channel-contacts",
			summary: "List channel contacts across orgs",
			tags: ["Superadmin"],
		})
		.input(
			z.object({
				orgId: z.string().optional(),
				limit: z.coerce.number().int().min(1).max(1000).optional().default(500),
			})
		)
		.output(
			z.object({
				contacts: z.array(ChannelContactSchema),
				total: z.number().int(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	// ==========================================================================
	// System Health
	// ==========================================================================

	/** Get system health metrics */
	getSystemHealth: oc
		.route({
			method: "GET",
			path: "/api/superadmin/system-health",
			summary: "Get system health metrics",
			tags: ["Superadmin"],
		})
		.output(SystemHealthSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),
};

// ============================================================================
// Type Exports
// ============================================================================

export type SuperadminContract = typeof superadminContract;

// Inferred types for use in services/components
export type OrgBreakdown = z.infer<typeof OrgBreakdownSchema>;
export type SuperadminAiObservability = z.infer<
	typeof SuperadminAiObservabilitySchema
>;
export type SuperadminDashboard = z.infer<typeof SuperadminDashboardSchema>;
export type OrganizationWithStats = z.infer<typeof OrganizationWithStatsSchema>;
export type SuperadminMember = z.infer<typeof SuperadminMemberSchema>;
export type ChannelContact = z.infer<typeof ChannelContactSchema>;
export type JobQueueStatus = z.infer<typeof JobQueueStatusSchema>;
export type SystemHealth = z.infer<typeof SystemHealthSchema>;
